import type { ComponentType } from 'react';
// The plain i18next singleton, NOT the app-specific '@/i18n/marketing' init
// module — that module's import has a side effect (calls .init() on this
// same singleton), which would clobber whatever i18next instance is already
// active (e.g. '@/i18n/config' in tests/SSR) just by being imported.
import i18next from 'i18next';

export interface ToolPageComponentProps {
  namespace?: string;
}

type ToolModule = { default: ComponentType<ToolPageComponentProps> };

/**
 * Lazily registers the 'tools-floor-plan' i18next namespace, instead of
 * statically importing it into i18n/marketing.ts (which would ship it on
 * every marketing page's initial JS — see
 * apps/web/scripts/check-public-bundles.mjs budget). Called both from this
 * tool's own loader and from ToolsHubPage's loader (marketing-main.tsx),
 * since the /tools grid renders every tool's card title/description too.
 */
export async function loadFloorPlanLocales(): Promise<void> {
  if (i18next.hasResourceBundle('en', 'tools-floor-plan')) return;
  const [{ default: en }, { default: esMX }] = await Promise.all([
    import('@/i18n/locales/en/tools-floor-plan.json'),
    import('@/i18n/locales/es-MX/tools-floor-plan.json'),
  ]);
  i18next.addResourceBundle('en', 'tools-floor-plan', en);
  i18next.addResourceBundle('es-MX', 'tools-floor-plan', esMX);
}

export const TOOL_PAGE_LOADERS: Record<string, () => Promise<ToolModule>> = {
  'hair-dye-calculator': () =>
    import('./formula-scaler/FormulaScalerPage').then((module) => ({
      default: module.FormulaScalerPage,
    })),
  'salon-price-list-generator': () =>
    import('./salon-price-list/SalonPriceListPage').then((module) => ({
      default: module.SalonPriceListPage,
    })),
  'salon-floor-plan-planner': () =>
    Promise.all([import('./floor-plan/FloorPlanPage'), loadFloorPlanLocales()]).then(
      ([module]) => ({ default: module.FloorPlanPage })
    ),
};
